/**
 * <ActivationCommitmentSection> — « Durée d'engagement » (organism).
 *
 * Un seul cran (douze mois) et l'interrupteur qui en sort. Le cran s'éteint quand le parent choisit
 * zéro engagement : il reste lisible, mais il n'est plus ce qui est retenu.
 *
 * « Ce que vous gardez » vit DANS cette section, et nulle part ailleurs sur l'écran : c'est la seule
 * qui demande douze mois, et la seule dont la sortie coûte la remise. La répéter dans le
 * récapitulatif, c'était dire deux fois la même promesse sur un écran qu'on cherche à alléger.
 *
 * L'engagement verrouille la DURÉE, pas le CONTENU : les promesses de changement et de pause
 * restent vraies sous engagement.
 *
 * La section entière disparaît quand l'engagement court déjà côté famille : il n'y a rien à
 * choisir, la reprise est engagée d'office et le back force la valeur. Le view-model le dit en ne
 * passant aucune option.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { List } from "../../molecules/List"
import { OptionCard } from "../../molecules/OptionCard"
import { Stack } from "../../atoms/containers/Stack"
import { SubscriptionLine } from "../../molecules/SubscriptionLine"
import { Switch } from "../../atoms/Switch"
import { type UIActivationChoice, type UISubscriptionLine } from "../../../ports/primary/ui/types"

/** Largeur plancher d'un cran — le même plancher que les autres sections de choix. */
const OPTION_MIN_WIDTH = 96

export type ActivationCommitmentSectionProps = {
    /** Les crans proposés — « 12 / mois ». Vide → la section ne rend rien. */
    options?: UIActivationChoice<number>[]
    withCommitment: boolean
    onChangeCommitment: (next: boolean) => void
    /** « Ce que vous gardez » — les promesses de non-enfermement, mot pour mot. */
    guarantees?: UISubscriptionLine[]
    testID?: string
}

export function ActivationCommitmentSection(props: ActivationCommitmentSectionProps) {
    const { options = [], withCommitment, onChangeCommitment, guarantees = [], testID } = props

    if (options.length === 0) return null

    return (
        <List title="Durée d'engagement" titleIcon="handshake" testID={testID}>
            <View style={styles.row}>
                {options.map((option) => (
                    <View key={String(option.value)} style={styles.option}>
                        <OptionCard
                            label={option.label}
                            description={option.description ?? undefined}
                            selected={withCommitment}
                            disabled={!withCommitment}
                            onPress={() => onChangeCommitment(true)}
                        />
                    </View>
                ))}
            </View>

            <Switch
                checked={!withCommitment}
                onChange={(noCommitment) => onChangeCommitment(!noCommitment)}
                label="Je préfère zéro engagement"
            />

            {guarantees.length > 0 && (
                <Stack gap="2">
                    {guarantees.map((line) => (
                        <SubscriptionLine key={line.icon} line={line} />
                    ))}
                </Stack>
            )}
        </List>
    )
}

const styles = StyleSheet.create((theme) => ({
    row: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing["2"],
    },
    option: { minWidth: OPTION_MIN_WIDTH },
}))
